import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Copy, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fdate } from "@/lib/format";
import { mediaUrl } from "@/lib/site";
import { Sections } from "@/components/public/Sections";
import { EmptyState, Eyebrow } from "@/components/common";

const setSeo = (title, description) => {
  document.title = title ? `${title} | Monrezo` : "Monrezo | Club d'affaires privé";
  document.querySelector('meta[name="description"]')?.setAttribute("content", description || "");
};

export default function SitePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/|\/$/g, "") || "accueil";
  const [page, setPage] = useState(null);
  useEffect(() => {
    setPage(null);
    window.scrollTo(0, 0);
    api.get("/public/page", { params: { slug } }).then(({ data }) => {
      setPage(data);
      setSeo(slug === "accueil" ? "" : data.title, data.seo_description);
    }).catch(() => setPage(false));
  }, [slug]);
  if (page === null) return <div className="h-[70vh]" data-testid="cms-loading" />;
  if (!page) return <div className="max-w-xl mx-auto px-5 py-24"><EmptyState title="Page introuvable" text="Cette page n'existe pas ou n'est plus publiée." testId="page-not-found" /></div>;
  return <div data-testid={`cms-page-${slug.replace(/\//g, "-")}`}><Sections blocks={page.blocks} slug={slug} /></div>;
}

export function ArticlePage() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/public/articles/${slug}`).then(({ data }) => {
      setA(data);
      setSeo(data.seo_title || data.title, data.seo_description || data.excerpt);
    }).catch(() => setA(false));
  }, [slug]);
  if (a === null) return <div className="h-[70vh]" />;
  if (!a) return <div className="max-w-xl mx-auto px-5 py-24"><EmptyState title="Article introuvable" testId="article-not-found" /></div>;
  const url = window.location.href;
  const share = [["WhatsApp", `https://wa.me/?text=${encodeURIComponent(`${a.title} ${url}`)}`], ["LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`], ["Facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`]];
  return (
    <article className="max-w-3xl mx-auto px-5 py-12 lg:py-16 mr-fade-up" data-testid="article-page">
      <Link to="/ressources/blog" className="mr-btn-ghost -ml-3 mb-6 flex w-fit" data-testid="article-back-link"><ArrowLeft size={16} /> Blog</Link>
      <Eyebrow className="mb-4">{a.category}</Eyebrow>
      <h1 className="mr-h1 !text-4xl sm:!text-5xl">{a.title}</h1>
      <p className="text-sm text-mr-muted mt-4 tabular">{a.author} · {fdate(a.published_at)}</p>
      {a.cover_media_id && <img src={mediaUrl(a.cover_media_id)} alt="" className="mr-img w-full aspect-[16/9] object-cover border border-mr-border mt-8" />}
      {a.locked ? (
        <div className="mr-card mt-10 text-center py-10" data-testid="article-locked"><Lock className="mx-auto text-mr-accent mb-3" /><p className="font-display font-semibold text-lg">Article réservé aux membres</p><p className="text-sm text-mr-text2 mt-2">{a.excerpt}</p><Link to="/connexion" className="mr-btn-primary mt-6">Se connecter</Link></div>
      ) : (
        <div className="mr-prose text-mr-text2 text-[17px] mt-10" data-testid="article-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{a.body}</ReactMarkdown></div>
      )}
      {a.tags?.length > 0 && <div className="flex flex-wrap gap-2 mt-8">{a.tags.map((t) => <span key={t} className="text-xs px-2.5 py-1 border border-mr-border rounded-full text-mr-text2">#{t}</span>)}</div>}
      <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-mr-border" data-testid="article-share">
        <span className="text-sm text-mr-muted mr-2">Partager</span>
        {share.map(([n, u]) => <a key={n} href={u} target="_blank" rel="noreferrer" data-testid={`share-${n}`} className="mr-btn-secondary min-h-[40px] px-3">{n}</a>)}
        <button data-testid="share-copy" className="mr-btn-secondary min-h-[40px] px-3" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Lien copié"); }}><Copy size={14} /> Copier le lien</button>
      </div>
    </article>
  );
}
